<div
  #cellElement
  [class]="getCellClasses()"
  [style.background-color]="getCellBgStyle()"
  [style.color]="getCellTextColorStyle()"
  (click)="onCellClick()"
  (keydown)="onCellKeyDown($event)"
  [tabindex]="0"
  [attr.aria-readonly]="!canEdit()">
  @switch (dataType()) {
    @case ('planning-status') {
      <app-plan-status-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('deploy-status-overall') {
      <app-deploy-status-overall-cell
        #typedCellElement
        class="flex w-full"
        [value]="value()" />
    }
    @case ('string-with-badge') {
      <app-string-with-badge-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('string-number-map') {
      <app-key-value-map-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('uid') {
      <app-uid-cell
        #typedCellElement
        class="flex w-full"
        [value]="value()"
        [displayLabelFor]="displayLabelFor()"
        [targetEntityType]="targetEntityType()"
        [columnId]="columnId()"
        [row]="row()"
        (uidClick)="uidClick.emit($event)" />
    }
    @case ('uid-list') {
      <app-uid-list-cell
        #typedCellElement
        class="flex w-full"
        [value]="value()"
        [displayLabelFor]="displayLabelFor()"
        [targetEntityType]="targetEntityType()"
        [columnId]="columnId()"
        [row]="row()"
        (uidClick)="uidClick.emit($event)" />
    }
    @case ('text-list') {
      <app-text-list-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('tag-list') {
      <app-tag-list-cell
        #typedCellElement
        class="flex w-full"
        [value]="value()"
        [filterable]="filterable()"
        (uidClick)="uidClick.emit($event)" />
    }
    @case ('time-window') {
      <app-window-cell #typedCellElement [value]="value()" type="time" />
    }
    @case ('datetime-window') {
      <app-window-cell #typedCellElement [value]="value()" type="datetime" />
    }
    @case ('datetime-windows') {
      <app-window-cell #typedCellElement [value]="value()" type="datetime" />
    }
    @case ('date-window') {
      <app-window-cell #typedCellElement [value]="value()" type="date" />
    }
    @case ('datetime') {
      <app-datetime-cell #typedCellElement display="datetime" [value]="value()" />
    }
    @case ('date') {
      <app-datetime-cell #typedCellElement display="date" [value]="value()" />
    }
    @case ('time') {
      <app-datetime-cell #typedCellElement display="time" [value]="value()" />
    }
    @case ('boolean') {
      <app-boolean-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('enum') {
      <app-enum-cell #typedCellElement class="flex w-full" [value]="value()" />
    }
    @case ('duration') {
      <app-duration-cell
        #typedCellElement
        [value]="value()"
        class="flex w-full items-baseline justify-start" />
    }
    @case ('durations') {
      <app-duration-cell #typedCellElement [value]="value()" />
    }
    @case ('address') {
      <app-address-cell #typedCellElement [value]="value()" />
    }
    @case ('json') {
      <app-json-cell #typedCellElement class="w-full" [value]="value()" />
    }
    @case ('id') {
      <app-id-cell #typedCellElement [value]="value()" />
    }
    @case ('number') {
      <app-number-cell
        #typedCellElement
        class="flex w-full items-baseline justify-end"
        [value]="value()"
        [unit]="unit()" />
    }
    @default {
      @if (isComplexValue()) {
        <app-json-cell #typedCellElement class="w-full" [value]="value()" />
      } @else {
        <app-string-cell #typedCellElement [value]="value()" />
      }
    }
  }

  @if (isMetaPressed() && isMouseOver() && getCellActions(); as actions) {
    @if (actions.length > 0) {
      <app-actions-overlay
        class="h-4 opacity-0 group-hover/cell:opacity-100"
        [actions]="actions"
        (executeAction)="executeAction($event)" />
    }
  }

  <!-- Validation Error -->
  @if (validationMessage()) {
    <div
      class="absolute top-full right-1 left-0 z-10 mt-1 rounded bg-red-50 px-2 py-1 text-xs text-red-600 shadow-sm">
      {{ validationMessage() }}
    </div>
  }
</div>

@if (disabled()) {
  <app-tooltip [target]="cellElement" [content]="disabled()!.reason" />
}

<app-dropdown
  [target]="cellElement"
  [template]="cellEditor"
  [width]="editorWidth()"
  [minWidth]="cellWidth()"
  [minHeight]="cellHeight()"
  [isOpen]="isEditing()"
  [offset]="0"
  [goUpperTarget]="true"
  [closeOnEscape]="false"
  [closeOnClickOutside]="true"
  [onClose]="onCloseHandler"
  [useDefaultStyles]="false" />

<ng-template #cellEditor>
  <div class="contents" (keydown)="onEditorCellKeyDown($event)">
    @switch (dataType()) {
      @case ('date') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="date"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('datetime') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="datetime"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('time') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="time"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('time-window') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="time"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [range]="true"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('datetime-window') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="datetime"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [range]="true"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('date-window') {
        <app-datetime-cell-editor
          class="flex flex-col rounded-lg bg-white p-2 text-xs font-normal shadow-md dark:bg-zinc-900"
          type="date"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [range]="true"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('uid') {
        <app-uid-cell-editor
          class="flex flex-col rounded-lg text-xs font-normal shadow-md"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [tableCellHeight]="cellHeight()"
          [entityType]="editorConfig()?.uidEntityType ?? 'unknown'"
          [ownerEntityType]="ownerEntityType()"
          [columnId]="columnId() ?? ''"
          (valueChange)="updateEditValue($event)" />
      }
      @case ('enum') {
        <app-enum-cell-editor
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [enumValues]="editorConfig()?.enumValues ?? []"
          [enumLabels]="editorConfig()?.enumLabels"
          (valueChange)="onEnumValueChange($event)" />
      }
      @default {
        <app-string-cell-editor
          class="text-xs"
          [value]="value()"
          [tableCellWidth]="cellWidth()"
          [tableCellHeight]="cellHeight()"
          [minInputWidth]="0"
          (valueChange)="updateEditValue($event)" />
      }
    }
  </div>
</ng-template>
